/**
 * StepperInput: [ LABEL ]  ( ◄  value  ► )
 *
 * The label sits OUTSIDE, to the left; the arrows + value live together in a
 * rounded pill on the right (arrows circular, value centred). Used for BPM,
 * Offset, Speed, Step, Snap, NJS controls.
 */

import React, { useState } from 'react';
import { color, surface, font } from './theme';
import { IcTriLeft, IcTriRight } from './icons';

// Hover: fill the arrow (TINT by default, ACCENT when hoverAccent) + dark glyph.
const makeHoverIn = (accent: boolean) => (e: React.MouseEvent<HTMLButtonElement>) => {
  e.currentTarget.style.background = accent
    ? 'oklch(0.62 calc(0.14 * var(--accent-c)) var(--accent-h))'
    : 'oklch(0.62 calc(0.14 * var(--tint-c)) var(--tint-h))';
  e.currentTarget.style.color = 'oklch(0.14 calc(0.022 * var(--tint-c)) var(--tint-h))';
};
const arrowHoverOut = (e: React.MouseEvent<HTMLButtonElement>) => {
  e.currentTarget.style.background = 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)';
  e.currentTarget.style.color = 'var(--accent)';
};

interface StepperInputProps {
  label: string;
  value: string;
  onDecrement: () => void;
  onIncrement: () => void;
  /** On-change editing — fires for every keystroke. */
  onValueChange?: (raw: string) => void;
  editable?: boolean;
  /** Commit-on-blur editing — local while typing, fires on blur / Enter. */
  onCommitValue?: (raw: string) => void;
  /** Tooltip shown on the editable value field. */
  editTitle?: string;
  /** Override the label column width (default 50) to align with other rows. */
  labelWidth?: number;
  /** Fill the arrows with the ACCENT colour on hover instead of the tint. */
  hoverAccent?: boolean;
  /** A value with no decrement / increment arrows (a typed value only). */
  hideArrows?: boolean;
}

/** Editable value that only commits on blur / Enter (keeps a local draft while
 *  typing so an in-progress "12" doesn't momentarily apply as the value). */
function CommitField({ value, title, onCommit }: { value: string; title?: string; onCommit: (raw: string) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input
      // Numeric readout — plain keys type, Ctrl/Cmd shortcuts (undo/redo) still
      // fire even while focused (see useKeybindingEngine).
      data-num-readout=""
      value={draft ?? value}
      title={title}
      onChange={e => setDraft(e.target.value)}
      onFocus={() => setDraft(value)}
      onBlur={() => { if (draft != null) { onCommit(draft); setDraft(null); } }}
      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      style={inputStyle}
    />
  );
}

export default function StepperInput({
  label, value, onDecrement, onIncrement, onValueChange, editable = false, onCommitValue, editTitle, labelWidth, hoverAccent = false, hideArrows = false,
}: StepperInputProps) {
  const arrowHoverIn = makeHoverIn(hoverAccent);
  return (
    <div style={rowStyle}>
      <span style={{ ...labelStyle, ...(labelWidth ? { width: labelWidth } : null) }}>{label}</span>
      <div style={pillStyle}>
        {!hideArrows && (
          <button onClick={onDecrement} style={arrowBtnStyle} title={`Decrease ${label}`} data-no-tint-hover className="accent-fill-hover"
            onMouseEnter={arrowHoverIn} onMouseLeave={arrowHoverOut}>
            <IcTriLeft size={10} />
          </button>
        )}
        {onCommitValue ? (
          <CommitField value={value} title={editTitle} onCommit={onCommitValue} />
        ) : editable && onValueChange ? (
          <input
            value={value}
            onChange={e => onValueChange(e.target.value)}
            style={inputStyle}
          />
        ) : (
          <span style={valueStyle}>{value}</span>
        )}
        {!hideArrows && (
          <button onClick={onIncrement} style={arrowBtnStyle} title={`Increase ${label}`} data-no-tint-hover className="accent-fill-hover"
            onMouseEnter={arrowHoverIn} onMouseLeave={arrowHoverOut}>
            <IcTriRight size={10} />
          </button>
        )}
      </div>
    </div>
  );
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 4,
  minWidth: 0,
};

// Fixed-width label outside the pill so every row's pill starts at the same x.
// Title-case (no uppercase transform) to match the right-sidebar labels.
const labelStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 50,
  color: color.inkFaint,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.5,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// The pill on the right: arrows pinned to the ends, value centred.
const pillStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 6,
  background: surface.input,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
  borderRadius: 999,
  padding: '3px 3px',
  boxSizing: 'border-box',
};

// Subtle dark "camera button" look; the arrow glyph keeps the accent colour.
const arrowBtnStyle: React.CSSProperties = {
  flexShrink: 0,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  border: '1px solid var(--accent)',
  color: color.accent,
  borderRadius: '50%',
  width: 24,
  height: 24,
  cursor: 'pointer',
  fontSize: 9,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: font.sans,
  transition: 'all 0.15s ease',
  padding: 0,
};

const valueStyle: React.CSSProperties = {
  flex: 1,
  textAlign: 'center',
  color: color.ink,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: font.sans,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  textAlign: 'center',
  color: color.ink,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: font.sans,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  minWidth: 0,
};
